<template>
  <div
    v-if="isOpen"
    class="selfidges-popup"
    role="dialog"
    aria-modal="true"
    aria-label="Selfridges popup"
    @click.self="closePopup"
  >
    <div class="selfidges-popup__card">
      <IconButton
        v-if="imageLoaded"
        theme="secondary"
        icon="cross"
        class="selfidges-popup__close"
        aria-label="Close popup"
        @click="closePopup"
      />

      <a
        v-if="linkUrl"
        class="selfidges-popup__image-link"
        :href="linkUrl"
        @click="closePopup"
      >
        <img
          ref="image"
          class="selfidges-popup__image"
          :src="imageUrl"
          :alt="imageAlt"
          loading="eager"
          @load="imageLoaded = true"
        />
      </a>
      <img
        v-else
        ref="image"
        class="selfidges-popup__image"
        :src="imageUrl"
        :alt="imageAlt"
        loading="eager"
        @load="imageLoaded = true"
      />
    </div>
  </div>
</template>

<script>
import IconButton from "@/components/IconButton.vue";

export default {
  name: "SelfidgesPopUP",
  components: {
    IconButton,
  },
  props: {
    imageUrl: {
      type: String,
      default:
        "https://cdn.shopify.com/s/files/1/0942/9062/files/Group_821_8c351149-0481-46fe-8b6f-f758dfd70e67.jpg?v=1776691648",
    },
    imageAlt: {
      type: String,
      default: "Selfridges promotion",
    },
    linkUrl: {
      type: String,
      default: "",
    },
    requiredClicks: {
      type: Number,
      default: 1,
    },
    requireNavigation: {
      type: Boolean,
      default: true,
    },
    dismissStorageKey: {
      type: String,
      default: "selfidgesPopupDismissed",
    },
  },
  data() {
    return {
      isOpen: false,
      imageLoaded: false,
      initialPath: "",
      hasNavigated: false,
      removeAfterEachHook: null,
      sessionInitialPathKey: "selfidgesPopupInitialPath",
      sessionHasNavigatedKey: "selfidgesPopupHasNavigated",
      sessionShownKey: "selfidgesPopupShownThisSession",
    };
  },
  mounted() {
    const currentPath = window.location.pathname;
    const storedInitialPath = sessionStorage.getItem(
      this.sessionInitialPathKey
    );
    const storedHasNavigated =
      sessionStorage.getItem(this.sessionHasNavigatedKey) === "true";
    const shownThisSession =
      sessionStorage.getItem(this.sessionShownKey) === "true";

    if (storedInitialPath) {
      this.initialPath = storedInitialPath;
    } else {
      this.initialPath = currentPath;
      sessionStorage.setItem(this.sessionInitialPathKey, currentPath);
    }

    this.hasNavigated = storedHasNavigated || currentPath !== this.initialPath;
    if (this.hasNavigated) {
      sessionStorage.setItem(this.sessionHasNavigatedKey, "true");
    }

    if (localStorage.getItem(this.dismissStorageKey) === "true") {
      return;
    }
    if (shownThisSession) {
      return;
    }

    if (this.requireNavigation && this.hasNavigated) {
      this.openPopup();
      return;
    }

    if (this.$router && typeof this.$router.afterEach === "function") {
      this.removeAfterEachHook = this.$router.afterEach((to, from) => {
        const toPath = to?.path || "";
        const fromPath = from?.path || "";

        if (!toPath || toPath === fromPath) {
          return;
        }

        if (toPath !== this.initialPath) {
          this.hasNavigated = true;
          sessionStorage.setItem(this.sessionHasNavigatedKey, "true");
        }

        if (this.requireNavigation && fromPath === this.initialPath) {
          this.openPopup();
        }
      });
    }
  },
  beforeUnmount() {
    if (this.removeAfterEachHook) {
      this.removeAfterEachHook();
    }
  },
  methods: {
    openPopup() {
      if (this.isOpen) {
        return;
      }

      window.requestAnimationFrame(() => {
        this.isOpen = true;
        sessionStorage.setItem(this.sessionShownKey, "true");

        this.$nextTick(() => {
          const img = this.$refs.image;
          if (img && img.complete && img.naturalHeight > 0) {
            this.imageLoaded = true;
          }
        });
      });
    },
    closePopup() {
      this.isOpen = false;
      localStorage.setItem(this.dismissStorageKey, "true");
      sessionStorage.removeItem(this.sessionHasNavigatedKey);
      sessionStorage.removeItem(this.sessionInitialPathKey);
      sessionStorage.removeItem(this.sessionShownKey);
    },
  },
};
</script>

<style lang="scss" scoped>
.selfidges-popup {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.selfidges-popup__card {
  position: relative;
  width: min(390px, calc(100vw - 64px));
  max-height: calc(100vh - 64px);
}

.selfidges-popup__image-link,
.selfidges-popup__image {
  display: block;
  width: 100%;
}

.selfidges-popup__image {
  object-fit: contain;
}

.selfidges-popup__close {
  position: absolute;
  padding: 10px;
  border-radius: 50%;
  top: 8px;
  right: 8px;
  z-index: 1;
  cursor: pointer;

  @include respond-above(md) {
    top: 20px;
    right: 20px;
  }
}

@media (max-width: 767px) {
  .selfidges-popup {
    padding: 16px;
  }

  .selfidges-popup__card {
    width: min(320px, calc(100vw - 32px));
  }
}
</style>
